import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    smsGits1: false,
    smsGits2: false,
    smsGits3: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        message: formData.message,
        sms_gits_1: formData.smsGits1 ? 'Yes' : 'No',
        sms_gits_2: formData.smsGits2 ? 'Yes' : 'No',
        sms_gits_3: formData.smsGits3 ? 'Yes' : 'No',
      });
      const res = await fetch('https://readdy.ai/api/form/d6nfpi5v117fnkj2hi8g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 lg:px-12 bg-[#f5efe6]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-600 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            Contact
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Let's Work Together
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Ready to transform your business with AI? Reach out and let's start the conversation.
          </p>
          <div className="w-16 h-1 bg-teal-500 mx-auto rounded-full mt-6"></div>
        </div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
              <div className="space-y-5">
                <a href="mailto:support@gits.agency" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl group-hover:bg-teal-100 transition-colors">
                    <i className="ri-mail-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Email</div>
                    <div className="text-gray-800 font-medium text-sm group-hover:text-teal-600 transition-colors">support@gits.agency</div>
                  </div>
                </a>
                <a href="tel:+17433005858" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl group-hover:bg-teal-100 transition-colors">
                    <i className="ri-phone-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Phone</div>
                    <div className="text-gray-800 font-medium text-sm group-hover:text-teal-600 transition-colors">+1 (743) 300-5858</div>
                  </div>
                </a>
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-teal-50 rounded-xl">
                    <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">Address</div>
                    <div className="text-gray-800 font-medium text-sm">509 Vermeer Court<br />Mount Holly, NC 28120</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Connect</h3>
              <div className="flex gap-3">
                <a
                  href="https://www.linkedin.com/in/petergits"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-gray-50 hover:bg-teal-50 text-gray-600 hover:text-teal-600 rounded-xl border border-gray-200 hover:border-teal-200 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a
                  href="https://jessica.petergits.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-gray-50 hover:bg-teal-50 text-gray-600 hover:text-teal-600 rounded-xl border border-gray-200 hover:border-teal-200 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-robot-line text-xl"></i>
                </a>
                <a
                  href="https://betty.petergits.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-gray-50 hover:bg-amber-50 text-gray-600 hover:text-amber-500 rounded-xl border border-gray-200 hover:border-amber-200 transition-all duration-200 cursor-pointer"
                  title="Betty"
                >
                  <i className="ri-shopping-basket-line text-xl"></i>
                </a>
                <a
                  href="https://stephanie.petergits.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center bg-gray-50 hover:bg-rose-50 text-gray-600 hover:text-rose-500 rounded-xl border border-gray-200 hover:border-rose-200 transition-all duration-200 cursor-pointer"
                  title="Stephanie"
                >
                  <i className="ri-restaurant-line text-xl"></i>
                </a>
              </div>
            </div>

            <div className="bg-teal-600 rounded-2xl p-8 text-white">
              <i className="ri-lightbulb-flash-line text-3xl mb-4 block"></i>
              <h3 className="text-lg font-bold mb-2">Free AI Readiness Assessment</h3>
              <p className="text-teal-100 text-sm leading-relaxed">
                Not sure where to start with AI? Fill out the form and we will provide a complimentary assessment of your business's AI readiness.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                    <i className="ri-check-line text-teal-600 text-3xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-500">Thank you for reaching out. Peter will get back to you within 24 hours.</p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-5"
                >
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Smith"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Company</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                        className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Service Interested In</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all bg-white cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Consulting / Fractional Head of AI">Consulting / Fractional Head of AI</option>
                      <option value="AI Project Work">AI Project Work</option>
                      <option value="VoiceCal Booking System">VoiceCal Booking System</option>
                      <option value="AdSkipper for YouTube">AdSkipper for YouTube</option>
                      <option value="AI Strategy & Roadmap">AI Strategy &amp; Roadmap</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Message * <span className="text-gray-400 font-normal">({formData.message.length}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      maxLength={500}
                      rows={4}
                      placeholder="Tell Peter about your project or question..."
                      className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all resize-none"
                    />
                    {formData.message.length > 500 && (
                      <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {/* GITS SMS Consent */}
                  <div className="bg-gray-50 rounded-xl p-5 border border-gray-200 space-y-3">
                    <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">SMS &amp; Call Consent — GITS</p>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsGits1"
                        checked={formData.smsGits1}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-teal-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I consent to receive SMS messages and calls from GITS regarding my inquiry. Message frequency varies. Reply STOP to opt-out or HELP for assistance. Message and data rates may apply. This consent is not a condition of purchase.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsGits2"
                        checked={formData.smsGits2}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-teal-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I consent to receive marketing text messages, about special offers, discounts, and service updates, from GITS at the phone number provided. Message frequency may vary. Message &amp; data rates may apply. Text HELP for assistance, reply STOP to opt out.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="smsGits3"
                        checked={formData.smsGits3}
                        onChange={handleChange}
                        className="mt-0.5 w-4 h-4 accent-teal-500 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-xs text-gray-600 leading-relaxed">
                        I consent to receive non-marketing text messages from GITS about Low Volume (Mixed). Message frequency may vary, message &amp; data rates may apply. Text HELP for assistance, reply STOP to opt out.
                      </span>
                    </label>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                      <i className="ri-error-warning-line flex-shrink-0"></i>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || formData.message.length > 500}
                    className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white font-semibold rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
